import { useToast } from '@lifebit-ai/lds'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { extractErrorMessage } from 'core/utils'
import { requestAdditionalInformationOnDataAccessRequest } from 'services/data-access-requests'
import { DataAccessRequestQueryKeys } from 'services/data-access-requests/queryKeys'
import type { RequestAdditionalInformationOnDataAccessRequestParams } from 'services/data-access-requests/types'
import { invalidateRequestActivityQueries } from 'services/request-activity/invalidateRequestActivityQueries'
import type { ApiErrorResponse } from 'services/types'
import { isAdditionalInfoFormEnabled, isAirlockRequestsV3RoutesEnabled } from 'utils/env'

interface UseRequestAdditionalInformationOnDataAccessRequestParams {
  onSuccess?: () => void
  requestId: string
}

const ADDITIONAL_INFORMATION_REQUESTS_DISABLED_MESSAGE = 'Additional information requests are not enabled.'

const invalidateDataAccessRequestQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  requestId: string
): void => {
  queryClient.invalidateQueries({ queryKey: DataAccessRequestQueryKeys.Verification(requestId) })
  queryClient.invalidateQueries({ queryKey: DataAccessRequestQueryKeys.All() })
  invalidateRequestActivityQueries(queryClient)
}

export const useRequestAdditionalInformationOnDataAccessRequest = ({
  onSuccess,
  requestId
}: UseRequestAdditionalInformationOnDataAccessRequestParams) => {
  const toast = useToast()
  const queryClient = useQueryClient()

  return useMutation<void, ApiErrorResponse, RequestAdditionalInformationOnDataAccessRequestParams>({
    mutationFn: ({ reason }) => {
      if (!isAdditionalInfoFormEnabled() || !isAirlockRequestsV3RoutesEnabled()) {
        return Promise.reject(new Error(ADDITIONAL_INFORMATION_REQUESTS_DISABLED_MESSAGE))
      }

      return requestAdditionalInformationOnDataAccessRequest(requestId, reason)
    },
    onSuccess: () => {
      toast({ message: 'Additional information requested successfully', type: 'success' })
      invalidateDataAccessRequestQueries(queryClient, requestId)
      onSuccess?.()
    },
    onError: (error, _variables, _context) => {
      if (error instanceof Error && error.message === ADDITIONAL_INFORMATION_REQUESTS_DISABLED_MESSAGE) {
        return
      }

      if (error.response?.status === 409) {
        invalidateDataAccessRequestQueries(queryClient, requestId)
      }

      toast({
        message: extractErrorMessage(error, 'Failed to request additional information'),
        type: 'error'
      })
    }
  })
}
